import { useNavigate } from "react-router-dom";
import { useToast } from "../../components/toast";
import { useState } from "react";
import { CreateCompetitionDto } from "../../types/competition.types";
import { CompetitionsAPI } from "../../APIs/competitions.api";
import { EditableField } from "../../components/editable-field";
import { DateField } from "../../components/date-field";
import { useCompetitions } from "../../context/CompetitionsContext";
import { Button } from "../../components/button";
import { toUTCDateString } from "../../utils/date.utils";
import {
  PaymentType,
  PaymentTypeTranslations,
} from "../../types/payments.types";

export function CompetitionsAddForm() {
  const navigate = useNavigate();
  const { addToast } = useToast();
  const [error, setError] = useState<string | null>(null);
  const { getAdminCompetitions } = useCompetitions();
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

  const defaultCompetition: CreateCompetitionDto = {
    name: "",
    description: "",
    edition: 0,
    start_date: "",
    end_date: "",
    registration_start: "",
    registration_end: "",
    prices: [
      {
        price: 0,
        start_date: "",
        end_date: "",
        installment_count: 0,
        covers_platform_fee: false,
        platform_fee: 0,
        payment_type: PaymentType.CASH,
      },
    ],
  };

  const [competition, setCompetition] =
    useState<CreateCompetitionDto>(defaultCompetition);

  const handleError = (message: string, timeOut?: number) => {
    setError(message);
    setTimeout(() => setError(null), timeOut || 5000);
  };

  const validateDates = (): string | null => {
    if (competition.registration_end > competition.start_date) {
      return "La finalización del registro debe ser antes del inicio de la competencia.";
    }
    if (competition.registration_start > competition.registration_end) {
      return "El inicio del registro debe ser antes de su finalización.";
    }
    if (competition.start_date > competition.end_date) {
      return "El inicio de la competencia debe ser antes del final.";
    }
    return null;
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (isSubmitting) return;
    setIsSubmitting(true);

    const validationError = validateDates();
    if (validationError) {
      handleError(validationError);
      setIsSubmitting(false);
      return;
    }

    try {
      const response = await CompetitionsAPI.postCompetition({
        ...competition,
        registration_start:
          toUTCDateString(competition.registration_start) || "",
        registration_end: toUTCDateString(competition.registration_end) || "",
        start_date: toUTCDateString(competition.start_date) || "",
        end_date: toUTCDateString(competition.end_date) || "",
        prices: competition.prices.map((price) => ({
          ...price,
          start_date: toUTCDateString(price.start_date) || "",
          end_date: toUTCDateString(price.end_date) || "",
        })),
      });

      if (response.statusCode === 201) {
        addToast({
          type: "success",
          message: "Competencia creada correctamente",
          location: "bottom-center",
        });
        getAdminCompetitions();
        navigate("/dashboard/competitions");
      } else {
        handleError(`Error al crear la competencia: ${response.message}`);
      }
    } catch (error) {
      handleError("Error al crear la competencia");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleBaseChange = (e: React.ChangeEvent<HTMLElement>) => {
    const { name, value } = e.target as HTMLInputElement;
    setCompetition((prev) => ({ ...prev, [name]: value }));
  };

  const handlePriceChange = (index: number, field: string, value: any) => {
    const newPrices = [...competition.prices];
    newPrices[index] = { ...newPrices[index], [field]: value };
    setCompetition((prev) => ({ ...prev, prices: newPrices }));
  };

  const addPrice = () => {
    setCompetition((prev) => ({
      ...prev,
      prices: [
        ...prev.prices,
        {
          price: 0,
          start_date: "",
          end_date: "",
          installment_count: 0,
          platform_fee: 0,
          covers_platform_fee: false,
          payment_type: PaymentType.CASH,
        },
      ],
    }));
  };

  const removePrice = (index: number) => {
    if (competition.prices.length <= 1) return;
    setCompetition((prev) => ({
      ...prev,
      prices: prev.prices.filter((_, i) => i !== index),
    }));
  };

  return (
    <div className="p-4 border border-gray-300 rounded-md">
      <h3 className="text-xl font-bold text-gray-800 mb-4">
        Crear nueva competencia
      </h3>
      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative">
          {error}
        </div>
      )}
      <form onSubmit={handleSubmit} className="space-y-4">
        <EditableField
          label="Nombre"
          placeholder="Ingrese el nombre de la competencia"
          value={competition.name}
          onChange={handleBaseChange}
          required
          type="text"
          name="name"
          minLength={3}
          maxLength={100}
        />
        <EditableField
          label="Edición"
          value={competition.edition}
          placeholder="Ingrese la edición de la competencia"
          onChange={handleBaseChange}
          required
          type="number"
          min={1}
          name="edition"
        />
        <DateField
          label="Fecha de Inicio de Inscripciones"
          value={competition.registration_start}
          onChange={handleBaseChange}
          name="registration_start"
          required
          type="datetime-local"
        />
        <DateField
          label="Fecha de Finalización de Inscripciones"
          value={competition.registration_end}
          onChange={handleBaseChange}
          name="registration_end"
          required
          type="datetime-local"
        />
        <DateField
          label="Fecha de Inicio de la Competencia"
          value={competition.start_date}
          onChange={handleBaseChange}
          name="start_date"
          required
          type="datetime-local"
        />
        <DateField
          label="Fecha de Finalización de la Competencia"
          value={competition.end_date}
          onChange={handleBaseChange}
          name="end_date"
          required
          type="datetime-local"
        />

        <div className="border-t pt-4">
          <h4 className="text-lg font-semibold mb-2">Precios</h4>
          {competition.prices.map((price, index) => (
            <div key={index} className="border p-4 rounded-md mb-4">
              <div className="flex justify-between mb-2">
                <h5 className="font-medium">Precio {index + 1}</h5>
                {competition.prices.length > 1 && (
                  <button
                    type="button"
                    onClick={() => removePrice(index)}
                    className="text-red-600 hover:text-red-800"
                  >
                    Eliminar
                  </button>
                )}
              </div>
              <div className="my-4">
                <EditableField
                  label="Precio"
                  value={price.price}
                  onChange={(e: any) =>
                    handlePriceChange(index, "price", e.target.value)
                  }
                  required
                  type="number"
                  name={`price-${index}`}
                  min={1}
                />
                <EditableField
                  label="Cuotas"
                  value={price.installment_count}
                  onChange={(e: any) =>
                    handlePriceChange(
                      index,
                      "installment_count",
                      e.target.value
                    )
                  }
                  required
                  type="number"
                  name={`installments-${index}`}
                  min={1}
                />
                <EditableField
                  label="Tipo de pago"
                  value={price.payment_type}
                  onChange={(e: any) =>
                    handlePriceChange(index, "payment_type", e.target.value)
                  }
                  required
                  type="select"
                  name={`payment_type-${index}`}
                  options={Object.entries(PaymentTypeTranslations).map(
                    ([key, value]) => ({
                      key,
                      value,
                    })
                  )}
                  placeholder="Seleccione el tipo de pago"
                />

                <EditableField
                  label="Comisión de la plataforma"
                  value={price.platform_fee}
                  onChange={(e: any) =>
                    handlePriceChange(index, "platform_fee", e.target.value)
                  }
                  type="number"
                  name={`platform_fee-${index}`}
                />

                <EditableField
                  label="¿Asumir costos de la plataforma de pagos?"
                  placeholder="Ingrese el precio de inscripción de la competencia"
                  value={price.covers_platform_fee}
                  onChange={(e: any) =>
                    handlePriceChange(
                      index,
                      "covers_platform_fee",
                      e.target.value
                    )
                  }
                  type="checkbox"
                  name={`covers_platform_fee-${index}`}
                />
                <DateField
                  label="Fecha de Inicio del Precio"
                  value={price.start_date}
                  onChange={(e) =>
                    handlePriceChange(index, "start_date", e.target.value)
                  }
                  name={`price-start-${index}`}
                  required
                  type="datetime-local"
                />
                <DateField
                  label="Fecha de Finalización del Precio"
                  value={price.end_date}
                  onChange={(e) =>
                    handlePriceChange(index, "end_date", e.target.value)
                  }
                  name={`price-end-${index}`}
                  required
                  type="datetime-local"
                />
              </div>
            </div>
          ))}
          <Button
            type="button"
            variant="create"
            onClick={addPrice}
            text="Agregar otro precio"
            className="mb-4"
          />
        </div>

        <div className="flex justify-end space-x-2 mt-4">
          <Button
            type="button"
            variant="cancel"
            onClick={() =>
              navigate("/dashboard/competitions", { replace: true })
            }
            text="Cancelar"
          />
          <Button
            type="submit"
            text="Crear"
            variant="create"
            disabled={isSubmitting}
          />
        </div>
      </form>
    </div>
  );
}
